import clsx from "clsx";
import React, { Fragment, useEffect } from "react";
import { useSelector } from "react-redux";
import CuratedProductsCard from "web/react/components/curated-products-card/curated-products-card";
import { ExpandableText } from "web/react/components/expandable-text/expandable-text";
import FeedNoResults from "web/react/components/feed-no-results/feed-no-results";
import { FeedsShowMore } from "web/react/components/feeds-show-more/feeds-show-more";
import { KeylineGrid, KeylineGridItem } from "web/react/components/keyline-grid";
import { OmnibusDirectiveBanner } from "web/react/components/omnibus-directive-banner/omnibus-directive-banner";
import {
    ProductCardImage,
    ProductCardWishlist,
    ProductDetails,
    ProductPrice,
} from "web/react/components/product-card/react/emo";
import ProductCard from "web/react/components/product-card/react/product-card";
import { HStack } from "web/react/emo/h-stack";
import { VStack } from "web/react/emo/v-stack";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useInitialReduxState } from "web/react/hooks/use-inital-redux-state/use-initial-redux-state";
import { setFeedData } from "web/redux/ducks/feed";
import { setAll as setShowMoreAll } from "web/redux/ducks/feed-show-more";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import globals from "web/script/modules/globals";
import { languageIsEnglish } from "web/script/modules/language";
import { shouldShowOmnibusPages } from "web/script/modules/utils";
import {
    CuratedProductsLinksSerializer,
    ProductCardSerializer,
    ProductFeedSerializer,
} from "web/types/serializers";
import styles from "./feed-grid.module.css";

interface FeedGridConnected {
    description: string | null;
    shortDescription: string | null;
}

export interface FeedGridProps extends FeedGridConnected {
    data: Pick<
        ProductFeedSerializer,
        | "feed_items"
        | "type"
        | "pre_filters"
        | "curated_products"
        | "show_more_button"
        | "lyst_name"
    >;
}

export default function FeedGrid({
    data,
    description,
    shortDescription,
}: FeedGridProps): JSX.Element {
    useInitialReduxState([
        [setFeedData, data],
        [setShowMoreAll, data.show_more_button],
    ]);

    if (!data.feed_items.length) {
        return <FeedNoResults />;
    }

    return (
        <>
            {shouldShowOmnibusPages() ? <OmnibusDirectiveBanner /> : null}
            <FeedGridConnected description={description} shortDescription={shortDescription} />
        </>
    );
}

function FeedGridConnected({
    description,
    shortDescription,
}: FeedGridConnected): JSX.Element | null {
    const {
        data: { feed_items: feedItems, type, curated_products: curatedProducts, lyst_name: title },
        ui: { isFetching },
    } = useSelector((state: ReduxStoreState) => state.feed);

    return (
        <>
            <FeedGridInner
                feedItems={feedItems}
                curatedProducts={curatedProducts}
                isFetching={isFetching}
                feedType={type || ""}
                description={description}
                shortDescription={shortDescription}
                title={title}
            />
            <FeedsShowMore />
        </>
    );
}

interface FeedProductCardProps {
    product: ProductCardSerializer;
    feedType: string;
}

export function FeedProductCard({ product, feedType }: FeedProductCardProps): JSX.Element {
    return (
        <ProductCard product={product} dataTestId="product-card">
            <VStack spacing="xxs">
                <ProductCardImage feedType={feedType} shouldRenderLeadLinkInImage />
                <VStack spacing="xxxs">
                    <HStack spacing="none" justify="space-between">
                        <ProductPrice size="sm" />
                        {languageIsEnglish() && <ProductCardWishlist />}
                    </HStack>
                    <ProductDetails feedType={feedType} />
                </VStack>
            </VStack>
        </ProductCard>
    );
}

export function getProductIdFromUrl(): number | null {
    const productId = new URLSearchParams(globals?.window?.location?.search).get("productId");
    return productId ? Number(productId) : null;
}

export function getProductIdsForCriteo(items: ProductFeedSerializer["feed_items"]): number[] {
    return items.slice(0, 3).map(({ product_card: { id } }) => id);
}

function getCuratedProductCTA(
    curatedProducts: ProductFeedSerializer["curated_products"],
    i: number
): CuratedProductsLinksSerializer | null {
    if (curatedProducts && i % 14 === 0 && curatedProducts[i / 14 - 1]) {
        return curatedProducts[i / 14 - 1];
    }
    return null;
}

interface FeedGridInnerProps {
    isFetching: boolean;
    feedItems: ProductFeedSerializer["feed_items"];
    feedType: string;
    curatedProducts?: ProductFeedSerializer["curated_products"];
    description?: string | null;
    shortDescription?: string | null;
    title?: string | null;
}

function FeedGridInner({
    feedItems,
    isFetching,
    feedType,
    curatedProducts,
    description,
    shortDescription,
    title,
}: FeedGridInnerProps): JSX.Element {
    const criteoIds = getProductIdsForCriteo(feedItems);

    useEffect(() => {
        analytics.tagEvent(analytics.TAG_NETWORKS.CRITEO, "criteo_product_ids", {
            ids: criteoIds,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(criteoIds)]);

    const { isMobileViewport } = useDomViewport();
    const addDescription = (shortDescription || description) && isMobileViewport;

    const feedItemsFirst = feedItems.slice(0, 2);
    const feedItemsRest = feedItems.slice(2, feedItems.length);

    const productId = getProductIdFromUrl();

    const fullDescription = [shortDescription, description].join(" ");

    return (
        <KeylineGrid isEmo className={clsx(isFetching && styles.loading)}>
            {feedItemsFirst.map(({ product_card: product }, i) => {
                const cta = getCuratedProductCTA(curatedProducts, i);
                return (
                    <Fragment key={product.uid}>
                        {cta && (
                            <KeylineGridItem isEmo colspan={2}>
                                <CuratedProductsCard isEmo links={cta.links} />
                            </KeylineGridItem>
                        )}
                        <KeylineGridItem isEmo scrollIntoView={productId === product.id}>
                            <FeedProductCard product={product} feedType={feedType} />
                        </KeylineGridItem>
                    </Fragment>
                );
            })}
            {addDescription && fullDescription && (
                <KeylineGridItem isEmo colspan={2}>
                    <ExpandableText
                        title={`${gettext("general.about_retailer", {
                            retailer_name: title,
                        })}:`}
                        variant="box"
                        lineLimit={1}
                        analyticsCategory="feed-description"
                    >
                        {fullDescription}
                    </ExpandableText>
                </KeylineGridItem>
            )}
            {feedItemsRest.map(({ product_card: product }, i) => {
                const cta = getCuratedProductCTA(curatedProducts, i);
                return (
                    <Fragment key={product.uid}>
                        {cta && (
                            <KeylineGridItem isEmo colspan={2}>
                                <CuratedProductsCard isEmo links={cta.links} />
                            </KeylineGridItem>
                        )}
                        <KeylineGridItem isEmo scrollIntoView={productId === product.id}>
                            <FeedProductCard product={product} feedType={feedType} />
                        </KeylineGridItem>
                    </Fragment>
                );
            })}
        </KeylineGrid>
    );
}
